import { Segmented } from '@/components/Segmented/Segmented';
import { addDays, dayCount, MAX_RANGE_DAYS, type DayRange, type Preset } from '@/usage/series';

import styles from './RangePicker.module.css';

export interface RangePickerProps {
  range: DayRange;
  /** Null once the range has been picked by hand. */
  preset: Preset | null;
  today: string;
  onPreset: (preset: Preset) => void;
  /** Switches to picking dates by hand, keeping the current range. */
  onCustom: () => void;
  onRange: (range: DayRange) => void;
}

const OPTIONS: { value: Preset | 'custom'; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '28d', label: '28 days' },
  { value: 'custom', label: 'Custom' },
];

/** Presets first, then a from/to pair; the span never exceeds MAX_RANGE_DAYS. */
export function RangePicker({
  range,
  preset,
  today,
  onPreset,
  onCustom,
  onRange,
}: RangePickerProps) {
  function setFrom(from: string) {
    if (!from) {
      return;
    }
    let to = range.to < from ? from : range.to;
    if (dayCount({ from, to }) > MAX_RANGE_DAYS) {
      to = addDays(from, MAX_RANGE_DAYS - 1);
    }
    onRange({ from, to: to > today ? today : to });
  }

  function setTo(to: string) {
    if (!to) {
      return;
    }
    let from = range.from > to ? to : range.from;
    if (dayCount({ from, to }) > MAX_RANGE_DAYS) {
      from = addDays(to, 1 - MAX_RANGE_DAYS);
    }
    onRange({ from, to });
  }

  return (
    <div className={styles.picker}>
      <Segmented
        label="Date range"
        options={OPTIONS}
        value={preset ?? 'custom'}
        onChange={(value) => (value === 'custom' ? onCustom() : onPreset(value))}
      />
      <div className={`${styles.dates} ${preset ? styles.collapsed : ''}`}>
        <label className={styles.date}>
          <span className={styles.visuallyHidden}>From</span>
          <input
            type="date"
            value={range.from}
            max={today}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <span className={styles.dash} aria-hidden="true">
          –
        </span>
        <label className={styles.date}>
          <span className={styles.visuallyHidden}>To</span>
          <input
            type="date"
            value={range.to}
            max={today}
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
      </div>
    </div>
  );
}
